import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import createFakeEvent from '../../lib/createFakeEvent';
import H from '../H/H';
import Icon from '../Icon/Icon';
import TransitionGroup from '../TransitionGroup/TransitionGroup';

/**
 * `Accordion` is a container that shows its content when the component is expanded by clicking on its header. Optional
 * buttons and menus can be added to the header.
 *
 * @property {node} children The components you want to display when the container is expanded. The container is
 * collapsed by default.
 * @property {string} className The class name of this container. The class name will be appended to "core-accordion".
 * The class "expanded" will be added when the container is *expanded* is `true`. The class "disabled" be added when
 * *disabled* is `true`. The class "no-header" will be added when *showHeader* is `false`.
 * @property {boolean} disabled If `true`, this container will be disabled. The user will not be able to expand the
 * container or click on items in the toolbar when the container is disabled. Defaults to `false`.
 * @property {boolean} expanded If `true`, this container is expanded. The content will only be visible when the
 * container is expanded. Defaults to `false`.
 * @property {function} onClick A callback function that is called when user has expanded or collapsed the container. The
 * `event.target.value` will hold the new expanded state.
 * @property {boolean} showHeader If `false`, no header will be shown. Defaults to `true`.
 * @property {node} title The title displayed in the `Accordion`'s header. You may also pass a component that will be
 * rendered inside the accordion header. If your component does not stop the event propagation clicking on the custom title
 * component will also toggle *expanded* state. Set "data-preventmenuclose" to prevent this. Defaults to no title.
 * @property {node} toolbar One or more components that will be displayed to the right in the header. Defaults to no
 * toolbar.
 *
 * @see Button, PopupMenu
 */

 Accordion.propTypes = {
   children: PropTypes.node,
   className: PropTypes.string,
   disabled: PropTypes.bool,
   expanded: PropTypes.bool,
   onClick: PropTypes.func,
   showHeader: PropTypes.bool,
   title: PropTypes.node,
   toolbar: PropTypes.node,
 }
 
 Accordion.defaultProps = {
   showHeader: true,
 };

 export default function Accordion(props) {
  const { children, className, disabled, onClick, showHeader = true, title, toolbar, ...rest } = props;
  const [expanded, setExpanded] = useState(rest.expanded);
  const refToolbar = useRef(null);
  const refTitle = useRef(null);
  const refNode = useRef(null);
  
  useEffect(() => {
    setExpanded(rest.expanded);
  }, [rest.expanded]);

  const onHeaderClick = e => {
    const customTitle = typeof title !== 'string' && e.target.localName !== 'label';
    const clickedOnCustomTitle = customTitle && refTitle.current.contains(e.target) && title.props && title.props['data-preventmenuclose'];
    
    if (!disabled && (!refToolbar.current || !refToolbar.current.contains(e.target)) && !clickedOnCustomTitle) {
      const toggleExpanded = !expanded;
      const event = createFakeEvent(toggleExpanded, e, refNode.current.dataset);
      setExpanded(toggleExpanded);
      if (onClick) onClick(event, toggleExpanded);
    }
  };
    
  const contentRenderer = () => <div className="core-accordion-content">{children}</div>;
  const tools = toolbar ? <div className="toolbar" ref={refToolbar}>{toolbar}</div> : undefined;
  const cls = cx('core-accordion', className, { expanded: expanded, disabled, 'no-header': !showHeader });
   
  return (
    <div className={cls} ref={refNode}>
        {showHeader &&
          <H className="header" onClick={onHeaderClick}>
            <Icon className="dropdown-icon" name="dropdown"/>
            <span ref={refTitle} className="title">{title}</span>
            {tools}
          </H>
        }
        <TransitionGroup
          className="animate-expanded-content"
          contentRenderer={contentRenderer}
          in={expanded}
          timeout={100}/>
      </div>
    );
}